// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentCountries } from "@insite/client-framework/Services/WebsiteService";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { extractGoogleAddressDetails, saveZipCodeAddress } from "@west/App/Handlers/ZipCode/ZipCodeHelper";
import { getGeoCodeFromAddress } from "@west/App/Hooks/GoogleMaps/getGeoCodeFromAddress";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { getDataLayer } from "@west/XDL/Utilities/tracking";

type HandlerType = ApiHandlerDiscreteParameter<
    { postalCode: string } & HasOnSuccess & HasOnError<string>,
    string,
    google.maps.GeocoderResult[] | undefined
>;

export const GetGoogleLocation: HandlerType = props => {
    return new Promise<false | void>((resolve, _) => {
        getGeoCodeFromAddress(props.parameter.postalCode.trim())
            .then(r => {
                props.apiResult = r;
                resolve();
            })
            .catch(e => {
                props.apiResult = e === google.maps.GeocoderStatus.ZERO_RESULTS ? [] : undefined;
                resolve();
            });
    });
};

export const GetZipCode: HandlerType = async props => {
    const countryList = await getCurrentCountries({});
    const { error, ...result } = extractGoogleAddressDetails(props.apiResult, countryList);
    if (error) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(error);
        return false;
    }

    saveZipCodeAddress(props.dispatch, result);
};

/**
 * We loadCart to tell BE about a new value
 * @param props
 * @constructor
 */
export const LoadCart: HandlerType = async props => {
    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ forceRecalculation: true })(props.dispatch, props.getState);
};

export const GaUpdateZipCodeTracking: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.updateZipCodeEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.updateZipCodeEventName,
            zip_code: props.parameter.postalCode.trim(),
            affiliation: state.xGoogleAnalytics.affiliation,
            authentication_state: state.xGoogleAnalytics.authenticationState,
        };

        dataLayer.push(googleAnalyticsData);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [GetGoogleLocation, GetZipCode, LoadCart, GaUpdateZipCodeTracking, ExecuteOnSuccessCallback];

const updateZipCode = createHandlerChainRunner(chain, "UpdateZipCode");
export default updateZipCode;
